/**
 * usePushNotifications — hook for browser push notifications via SSE
 *
 * - Requests browser Notification permission on first use
 * - Subscribes to GET /api/notifications/stream (SSE)
 * - Shows native Notification for: task_complete, agent_done, new_message, budget_warning
 * - Persists user preference in localStorage under "msapling_notifications_enabled"
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LocalStorageKey } from '../stores/globalStore';

/**
 * localStorage key under which the user's push-notification on/off preference
 * is persisted. Kept in sync with LocalStorageKey.NOTIFICATIONS_ENABLED (the
 * canonical enum); exported as a named constant so callers/tests can reference
 * the key without reaching into the store enum.
 */
export const NOTIFICATION_PREF_KEY: string = LocalStorageKey.NOTIFICATIONS_ENABLED;

export type PushNotificationType =
  | 'task_complete'
  | 'agent_done'
  | 'new_message'
  | 'budget_warning';

export const NOTIFICATION_TYPES: PushNotificationType[] = [
  'task_complete',
  'agent_done',
  'new_message',
  'budget_warning',
];

export const isPushNotificationType = (value: unknown): value is PushNotificationType =>
  typeof value === 'string' && (NOTIFICATION_TYPES as string[]).includes(value);

export interface PushNotificationPayload {
  event: string;
  data: {
    message?: string;
    title?: string;
    task_type?: string;
    status?: string;
    [key: string]: unknown;
  };
  ts: number;
}

interface UsePushNotificationsReturn {
  enabled: boolean;
  permission: NotificationPermission | 'unsupported';
  enableNotifications: () => Promise<boolean>;
  disableNotifications: () => void;
  lastEvent: PushNotificationPayload | null;
  connectionError: string | null;
}

function getApiBase(): string {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  return origin;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(LocalStorageKey.NOTIFICATIONS_ENABLED) === 'true';
  });

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });

  const [lastEvent, setLastEvent] = useState<PushNotificationPayload | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const sseRef = useRef<EventSource | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const showBrowserNotification = useCallback((payload: PushNotificationPayload) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    if (!isPushNotificationType(payload.event)) return;
    const eventType = payload.event;
    let title = 'MSapling';
    let body = payload.data?.message || 'Background event received.';

    // Strip HTML tags from body for defense-in-depth (Notification body is plain text by default)
    const sanitizeBody = (text: string): string => String(text).replace(/<[^>]*>/g, '');

    switch (eventType) {
      case 'task_complete':
        title = 'Task Complete';
        body = payload.data?.message || `Task '${payload.data?.task_type || 'unknown'}' finished (${payload.data?.status || 'done'}).`;
        break;
      case 'agent_done':
        title = 'Agent Done';
        body = payload.data?.message || 'Agent completed its work.';
        break;
      case 'new_message':
        title = 'New Message';
        body = payload.data?.message || 'You have a new message.';
        break;
      case 'budget_warning':
        title = 'Budget Warning';
        body = payload.data?.message || 'Your usage budget is running low.';
        break;
      default:
        // Unknown event type — skip native notification but still store lastEvent
        return;
    }

    try {
      new Notification(title, {
        body: sanitizeBody(body),
        icon: '/favicon.ico',
        tag: `msapling-${eventType}-${payload.ts}`,
      });
    } catch (err) {
      // Notifications may be blocked at OS level or by the browser
      console.warn('[usePushNotifications] Failed to show notification:', err);
    }
  }, []);

  const connectSSE = useCallback(() => {
    setConnectionError(null);
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const url = `${getApiBase()}/api/notifications/stream`;
    let es: EventSource;

    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (err) {
      console.warn('[usePushNotifications] Failed to open SSE connection:', err);
      setConnectionError('The notification stream could not be opened.');
      return;
    }

    sseRef.current = es;
    es.onopen = () => setConnectionError(null);

    es.onmessage = (event: MessageEvent) => {
      if (!enabledRef.current) return;
      try {
        const payload = JSON.parse(event.data) as PushNotificationPayload;
        setLastEvent(payload);
        showBrowserNotification(payload);
      } catch (err) {
        // Malformed SSE data — log so it is visible during debugging
        console.warn('[usePushNotifications] Failed to parse SSE message:', err);
      }
    };

    es.onerror = () => {
      // EventSource retries transient network failures. Expose fatal closure
      // as state so callers can distinguish a stopped stream from reconnecting.
      if (es.readyState === EventSource.CLOSED) {
        sseRef.current = null;
        setConnectionError('The notification stream closed and could not reconnect.');
      } else {
        setConnectionError('Reconnecting to the notification stream…');
      }
    };
  }, [showBrowserNotification]);

  const disconnectSSE = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
  }, []);

  const enableNotifications = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return false;
    }

    let perm = Notification.permission;
    if (perm === 'default') {
      try {
        perm = await Notification.requestPermission();
      } catch (err) {
        console.warn('[usePushNotifications] Notification permission request failed:', err);
        setPermission(Notification.permission);
        return false;
      }
    }
    setPermission(perm);

    if (perm !== 'granted') {
      return false;
    }

    localStorage.setItem(LocalStorageKey.NOTIFICATIONS_ENABLED, 'true');
    setEnabled(true);
    return true;
  }, []);

  const disableNotifications = useCallback(() => {
    localStorage.setItem(LocalStorageKey.NOTIFICATIONS_ENABLED, 'false');
    setEnabled(false);
    disconnectSSE();
  }, [disconnectSSE]);

  // Start / stop SSE based on enabled flag
  useEffect(() => {
    if (enabled && permission === 'granted') {
      connectSSE();
    } else {
      disconnectSSE();
    }
    return () => {
      disconnectSSE();
    };
  }, [enabled, permission, connectSSE, disconnectSSE]);

  // Keep permission state current when the user changes site settings while
  // the page is backgrounded. Returning focus also lets the SSE effect stop
  // the connection promptly if notification permission was revoked.
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    const syncPermission = () => setPermission(Notification.permission);
    syncPermission();
    document.addEventListener('visibilitychange', syncPermission);
    window.addEventListener('focus', syncPermission);
    return () => {
      document.removeEventListener('visibilitychange', syncPermission);
      window.removeEventListener('focus', syncPermission);
    };
  }, []);

  return {
    enabled,
    permission,
    enableNotifications,
    disableNotifications,
    lastEvent,
    connectionError,
  };
}
